import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    CardActions,
    Switch,
    FormControlLabel,
} from "@mui/material";
import {
    Add,
    Edit,
    Delete,
    LocalShipping,
    Telegram,
    History,
} from "@mui/icons-material";
import {
    deliveryService,
    DeliveryService,
} from "../../services/deliveryService";
import { validateAndNormalizeBdPhone } from "../../utils/phone";
import { CourierWebhookLogDialog } from "../../components/settings/CourierWebhookLogDialog";

export const DeliveryServiceManagement: React.FC = () => {
    const [deliveryServices, setDeliveryServices] = useState<DeliveryService[]>(
        [],
    );
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>("");
    const [formOpen, setFormOpen] = useState(false);
    const [editingService, setEditingService] =
        useState<DeliveryService | null>(null);
    const [formLoading, setFormLoading] = useState(false);
    const [webhookLogOpen, setWebhookLogOpen] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        type: "INHOUSE" as "INHOUSE" | "THIRD_PARTY",
        contact_phone: "",
        contact_email: "",
        tracking_url_template: "",
        label_url_template: "",
        tg_chat_id: "",
        tg_username: "",
        is_active: true,
    });

    useEffect(() => {
        loadDeliveryServices();
    }, []);

    const loadDeliveryServices = async () => {
        try {
            setLoading(true);
            const services = await deliveryService.getDeliveryServices(false); // Get all including inactive
            setDeliveryServices(services);
        } catch (err) {
            setError("Failed to load delivery services");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenForm = (service?: DeliveryService) => {
        if (service) {
            setEditingService(service);
            setFormData({
                name: service.name,
                type: service.type,
                contact_phone: service.contact_phone || "",
                contact_email: service.contact_email || "",
                tracking_url_template: service.tracking_url_template || "",
                label_url_template: service.label_url_template || "",
                tg_chat_id: service.tg_chat_id || "",
                tg_username: service.tg_username || "",
                is_active: service.is_active,
            });
        } else {
            setEditingService(null);
            setFormData({
                name: "",
                type: "INHOUSE",
                contact_phone: "",
                contact_email: "",
                tracking_url_template: "",
                label_url_template: "",
                tg_chat_id: "",
                tg_username: "",
                is_active: true,
            });
        }
        setFormOpen(true);
    };

    const handleCloseForm = () => {
        setFormOpen(false);
        setEditingService(null);
        setError("");
    };

    const handleFormSubmit = async () => {
        try {
            setFormLoading(true);
            setError("");

            if (!formData.name.trim()) {
                setError("Name is required");
                return;
            }

            let phoneCheck: ReturnType<typeof validateAndNormalizeBdPhone> | null = null;
            if (formData.contact_phone?.trim()) {
                phoneCheck = validateAndNormalizeBdPhone(formData.contact_phone);
                if (!phoneCheck.valid) {
                    setError(phoneCheck.error!);
                    return;
                }
            }
            const submitData = {
                ...formData,
                contact_phone: phoneCheck?.normalized,
            };

            if (editingService) {
                await deliveryService.updateDeliveryService(
                    editingService.delivery_service_id,
                    submitData,
                );
            } else {
                await deliveryService.createDeliveryService(submitData);
            }

            await loadDeliveryServices();
            handleCloseForm();
        } catch (err: any) {
            setError(
                err.response?.data?.detail || "Failed to save delivery service",
            );
        } finally {
            setFormLoading(false);
        }
    };

    const handleDelete = async (service: DeliveryService) => {
        if (
            window.confirm(`Are you sure you want to delete "${service.name}"?`)
        ) {
            try {
                await deliveryService.deleteDeliveryService(
                    service.delivery_service_id,
                );
                await loadDeliveryServices();
            } catch (err: any) {
                setError(
                    err.response?.data?.detail ||
                        "Failed to delete delivery service",
                );
            }
        }
    };

    const getTypeColor = (type: string) => {
        return type === "INHOUSE" ? "primary" : "secondary";
    };

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="400px"
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
                mb={3}
            >
                <Typography variant="h4" component="h1">
                    Delivery Service Management
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                    <Button
                        variant="outlined"
                        startIcon={<History />}
                        onClick={() => setWebhookLogOpen(true)}
                    >
                        Webhook activity
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => handleOpenForm()}
                    >
                        Add Delivery Service
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    onClose={() => setError("")}
                >
                    {error}
                </Alert>
            )}

            <Box display="flex" flexWrap="wrap" gap={3}>
                {deliveryServices.map((service) => (
                    <Box
                        key={service.delivery_service_id}
                        sx={{
                            flex: {
                                xs: "1 1 100%",
                                md: "1 1 calc(50% - 24px)",
                                lg: "1 1 calc(33.333% - 32px)",
                            },
                            minWidth: 0,
                        }}
                    >
                        <Card>
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={2}>
                                    <LocalShipping sx={{ mr: 1 }} />
                                    <Typography variant="h6" component="h2">
                                        {service.name}
                                    </Typography>
                                </Box>

                                <Box mb={1}>
                                    <Chip
                                        label={service.type}
                                        color={getTypeColor(service.type)}
                                        size="small"
                                    />
                                    <Chip
                                        label={
                                            service.is_active
                                                ? "Active"
                                                : "Inactive"
                                        }
                                        color={
                                            service.is_active
                                                ? "success"
                                                : "default"
                                        }
                                        size="small"
                                        sx={{ ml: 1 }}
                                    />
                                </Box>

                                {service.contact_phone && (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        gutterBottom
                                    >
                                        Phone: {service.contact_phone}
                                    </Typography>
                                )}

                                {service.contact_email && (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        gutterBottom
                                    >
                                        Email: {service.contact_email}
                                    </Typography>
                                )}

                                <Box display="flex" alignItems="center" mb={1}>
                                    <Telegram
                                        fontSize="small"
                                        sx={{
                                            mr: 0.5,
                                            color: service.tg_chat_id
                                                ? "#0088cc"
                                                : "text.disabled",
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        color={
                                            service.tg_chat_id
                                                ? "text.secondary"
                                                : "text.disabled"
                                        }
                                    >
                                        {service.tg_chat_id
                                            ? `${service.tg_username ? "@" + service.tg_username + " · " : ""}Telegram linked`
                                            : "Telegram not linked"}
                                    </Typography>
                                </Box>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Created:{" "}
                                    {new Date(
                                        service.created_at,
                                    ).toLocaleDateString()}
                                </Typography>
                            </CardContent>
                            <CardActions>
                                <Button
                                    size="small"
                                    startIcon={<Edit />}
                                    onClick={() => handleOpenForm(service)}
                                >
                                    Edit
                                </Button>
                                <Button
                                    size="small"
                                    startIcon={<Delete />}
                                    color="error"
                                    onClick={() => handleDelete(service)}
                                >
                                    Delete
                                </Button>
                            </CardActions>
                        </Card>
                    </Box>
                ))}
            </Box>

            {/* Add/Edit Dialog */}
            <Dialog
                open={formOpen}
                onClose={handleCloseForm}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {editingService
                        ? "Edit Delivery Service"
                        : "Add Delivery Service"}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <TextField
                            fullWidth
                            label="Name"
                            value={formData.name}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    name: e.target.value,
                                })
                            }
                            margin="normal"
                            required
                        />

                        <FormControl fullWidth margin="normal">
                            <InputLabel>Type</InputLabel>
                            <Select
                                value={formData.type}
                                label="Type"
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        type: e.target.value as
                                            | "INHOUSE"
                                            | "THIRD_PARTY",
                                    })
                                }
                            >
                                <MenuItem value="INHOUSE">In-house</MenuItem>
                                <MenuItem value="THIRD_PARTY">
                                    Third Party
                                </MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            label="Contact Phone"
                            value={formData.contact_phone}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    contact_phone: e.target.value,
                                })
                            }
                            margin="normal"
                        />

                        <TextField
                            fullWidth
                            label="Contact Email"
                            type="email"
                            value={formData.contact_email}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    contact_email: e.target.value,
                                })
                            }
                            margin="normal"
                        />

                        <TextField
                            fullWidth
                            label="Tracking URL Template"
                            placeholder="https://example.com/track/{consignment_id}"
                            value={formData.tracking_url_template}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    tracking_url_template: e.target.value,
                                })
                            }
                            margin="normal"
                            helperText="Use {consignment_id} as placeholder — e.g. https://steadfast.com.bd/track/{consignment_id}"
                        />

                        <TextField
                            fullWidth
                            label="Label Printing URL Template"
                            placeholder="https://example.com/label/{consignment_id}"
                            value={formData.label_url_template}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    label_url_template: e.target.value,
                                })
                            }
                            margin="normal"
                            helperText="Use {consignment_id} as placeholder — e.g. https://portal.packzy.com/api/v1/print_label/{consignment_id}"
                        />

                        <TextField
                            fullWidth
                            label="Telegram Chat ID"
                            placeholder="e.g. 123456789"
                            value={formData.tg_chat_id}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    tg_chat_id: e.target.value,
                                })
                            }
                            margin="normal"
                            helperText={
                                'Numeric chat ID needed to message this courier directly on Telegram. ' +
                                'A bot can\'t message someone by @username, and can\'t message them at all ' +
                                'until they\'ve messaged the bot first — have them open a chat with the bot ' +
                                'and send any message, then look up their numeric ID (e.g. via @userinfobot) and paste it here.'
                            }
                        />

                        <TextField
                            fullWidth
                            label="Telegram Username (optional label)"
                            placeholder="e.g. delivery_kawsar"
                            value={formData.tg_username}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    tg_username: e.target.value,
                                })
                            }
                            margin="normal"
                            helperText="Just a reference label for admins — not used to send the message. The Chat ID above is what actually matters."
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.is_active}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            is_active: e.target.checked,
                                        })
                                    }
                                />
                            }
                            label="Active"
                            sx={{ mt: 1 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseForm} disabled={formLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleFormSubmit}
                        variant="contained"
                        disabled={formLoading}
                    >
                        {formLoading ? (
                            <CircularProgress size={24} />
                        ) : editingService ? (
                            "Update"
                        ) : (
                            "Create"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            <CourierWebhookLogDialog
                open={webhookLogOpen}
                onClose={() => setWebhookLogOpen(false)}
            />
        </Box>
    );
};
